import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';

import { TextDocument } from '../../../models/text-document';
import { TextDocumentTemplate } from '../../../models/text-document-template';

import { AuthService } from '../../../services/common/auth.service';
import { NotyService } from '../../../services/common/noty.service';
import { TextDocumentService } from '../../../services/texts/text-document.service';
import { TextDocumentTemplateService } from '../../../services/texts/text-document-template.service';

declare var swal: any;

@Component({
  selector: 'app-text-document-index',
  templateUrl: './text-document-index.component.html',
  styleUrls: ['./text-document-index.component.scss'],
})
export class TextDocumentIndexComponent implements OnInit {
  @Input() matterId: number;
  @ViewChild('textArea') textAreaRef: ElementRef;

  buildingPreview: boolean = false;

  canManageTemplates: boolean = false;

  count: number = 0;

  deletingTemplate: boolean = false;

  formOptions: any = { variables: [] };

  involvementSearchTerm: string;

  loading: boolean = true;

  previewResult: string;

  templateDescription: string;
  templateDocumentId: number;

  templateName: string;

  recipients: Array<any> = [];

  replyingToTextDocumentId: number | undefined;
  replyContent: string = '';
  sendingReply: boolean = false;

  sendingText: boolean = false;

  savingTemplate: boolean = false;

  searchParams: any = {
    page: 1,
    per_page: 20,
  };

  selectedTemplateId: number | undefined;

  showForm: boolean = true;

  textContent: string = '';

  textDocuments: Array<TextDocument> = [];
  displayTextDocuments: Array<any> = [];

  maxTextLength: number = 150;

  constructor(private authService: AuthService, private notyService: NotyService, private textDocumentService: TextDocumentService, private textDocumentTemplateService: TextDocumentTemplateService) {}

  ngOnInit() {
    const user = this.authService.getUser();
    this.canManageTemplates = user && (user.isAdmin() || user.isTech());

    this.textDocumentService
      .searchOptions()
      .then((response_json) => {
        this.searchParams = response_json;
        this.searchParams.matter_id = this.matterId;
        this.searchParams.page = 1;
        this.loadTextDocuments();
      })
      .catch((err) => {
        this.loading = false;
        this.notyService.notify(`Error loading texts: ${err}`, 'error');
      });
    this.textDocumentService
      .formOptions()
      .then((response_json) => {
        this.formOptions = response_json;
      })
      .catch((err) => {
        this.loading = false;
        this.notyService.notify(`Error loading form: ${err}`, 'error');
      });

  }

  deleteTemplate() {
    swal({
      title: 'Delete',
      text: `Delete selected text template ?`,
      icon: 'warning',
      dangerMode: true,
      buttons: {
        cancel: {
          text: 'No',
          value: false,
          visible: true,
          className: '',
          closeModal: true,
        },
        confirm: {
          text: 'Yes',
          value: true,
          visible: true,
          className: '',
          closeModal: true,
        },
      },
    })
      .then((confirmed: any) => {
        if (confirmed) {
          this.deletingTemplate = true;
          this.textDocumentTemplateService
            .delete(this.selectedTemplateId!)
            .then((response) => {
              this.deletingTemplate = false;
              this.notyService.notify('Text template deleted', 'warning');
              this.reset();
            })
            .catch((err) => {
              this.deletingTemplate = false;
              this.notyService.notify(`Error deleting: ${err}`, 'error');
            });
        }
      })
      .catch((err: any) => {
        console.error('Delete prompt error', err);
        this.notyService.notify(`Error deleting: ${err}`, 'error');
      });
  }

  loadTemplate(textDocumentTemplateId: number) {
    this.selectedTemplateId = textDocumentTemplateId;
    this.textDocumentTemplateService
      .show(textDocumentTemplateId, this.matterId)
      .then((response_json) => {
        this.templateDescription = response_json.description;
        this.templateName = response_json.name;
        this.textContent = response_json.template_text;
      })
      .catch((err) => {
        this.notyService.notify(`Err loading template: ${err}`, 'error');
      });
  }

  loadTextDocuments(pageEvent: any = null) {
    if (pageEvent) this.searchParams.page = pageEvent.page;
    this.loading = true;
    this.textDocumentService
      .index(this.searchParams)
      .then((response_json) => {
        this.loading = false;
        this.count = response_json.count;
        this.setTextDocuments(response_json.text_documents);
      })
      .catch((err) => {
        this.loading = false;
        this.notyService.notify(`Error loading texts: ${err}`, 'error');
      });
  }

  previewText() {
    let recipient: any = this.recipients[0];
    if (!recipient) {
      this.notyService.notify('Please select atleast one recipient for preview', 'error');
      return;
    } else if (!recipient.value) {
      this.notyService.notify('Please select atleast one recipient for preview', 'error');
      return;
    }
    this.buildingPreview = true;
    this.textDocumentService
      .preview(this.textContent, recipient.value, this.matterId)
      .then((response_json) => {
        this.buildingPreview = false;
        if (response_json.success) {
          this.previewResult = response_json.text_content;
        } else {
          this.previewResult = '';
          this.notyService.notify(response_json.text || 'Error loading preview', 'error');
        }
      })
      .catch((err) => {
        this.buildingPreview = false;
        this.previewResult = '';
        this.notyService.notify(`Err loading preview: ${err}`, 'error');
      });
  }

  private setTextDocuments(textDocuments: Array<TextDocument>) {
    this.textDocuments = textDocuments || [];
    const childCounts = this.textDocuments.reduce((counts: any, textDocument: TextDocument) => {
      if (textDocument.parent_id) counts[textDocument.parent_id] = (counts[textDocument.parent_id] || 0) + 1;
      return counts;
    }, {});

    this.displayTextDocuments = this.textDocuments.map((textDocument: TextDocument) => {
      return {
        ...textDocument,
        thread_parent_label: textDocument.parent_id ? `Reply to #${textDocument.parent_id}` : '',
        thread_child_count: childCounts[textDocument.id] || 0,
        thread_has_connection: !!textDocument.parent_id || !!childCounts[textDocument.id],
      };
    });
  }

  threadRowClass(textDocument: any) {
    return {
      'table-info': textDocument.direction === 'inbound',
      'text-thread-row': textDocument.thread_has_connection,
      'text-thread-reply': !!textDocument.parent_id,
      'text-thread-parent': textDocument.thread_child_count > 0,
    };
  }

  recipientOnSelect(involvement_autocomplete_item: any) {
    this.involvementSearchTerm = '';
    for (let recipient of this.recipients) if (recipient.value == involvement_autocomplete_item.id) return;
    this.recipients.push({
      allow_phone_text: involvement_autocomplete_item.allow_phone_text,
      label: involvement_autocomplete_item.label,
      mobile_present: involvement_autocomplete_item.mobile_present,
      value: involvement_autocomplete_item.id,
    });
  }

  insertAtCursor(text: string) {
    const textarea = this.textAreaRef?.nativeElement;
    if (!textarea) {
      this.textContent = (this.textContent || '') + text;
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const current = this.textContent || '';
    this.textContent = current.substring(0, start) + text + current.substring(end);
    setTimeout(() => {
      const pos = start + text.length;
      textarea.selectionStart = pos;
      textarea.selectionEnd = pos;
      textarea.focus();
    });
  }

  removeRecipient(recipient: any) {
    this.recipients.splice(this.recipients.indexOf(recipient), 1);
  }

  cancelReply() {
    this.replyingToTextDocumentId = undefined;
    this.replyContent = '';
  }

  startReply(textDocument: TextDocument) {
    this.replyingToTextDocumentId = textDocument.id;
    this.replyContent = '';
  }

  sendReply(textDocument: TextDocument) {
    if (!this.replyContent) {
      this.notyService.notify('Text is required', 'error');
      return;
    }

    this.sendingReply = true;
    this.textDocumentService
      .reply(textDocument.id, this.replyContent)
      .then((response_json) => {
        this.sendingReply = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Reply sent', 'success');
          this.cancelReply();
          this.loadTextDocuments();
        } else {
          this.notyService.notify(response_json.text || 'Error sending reply', 'error');
        }
      })
      .catch((err) => {
        this.sendingReply = false;
        this.notyService.notify(`Error sending reply: ${err}`, 'error');
      });
  }

  reset() {
    this.templateDescription = '';
    this.templateName = '';
    this.recipients = [];
    this.selectedTemplateId = undefined;
    this.textContent = '';
    this.loadTextDocuments();
  }

  saveTemplate() {
    let newTemplate: any = {
      description: this.templateDescription,
      id: this.selectedTemplateId,
      name: this.templateName,
      template_text: this.textContent,
    };
    this.savingTemplate = true;
    this.textDocumentTemplateService
      .save(newTemplate)
      .then((response_json) => {
        this.savingTemplate = false;
        if (response_json.success) this.notyService.notify(response_json.text || 'Template saved', 'success');
        else this.notyService.notify(response_json.text || 'Template saved', 'error');
      })
      .catch((err) => {
        this.savingTemplate = false;
        this.notyService.notify(`Err saving: ${err}`, 'error');
      });
  }

  sendText() {
    if (this.recipients.length < 1) {
      this.notyService.notify('Atleast one recipient must be selected', 'error');
      return;
    }
    if (!this.textContent) {
      this.notyService.notify('Text is required', 'error');
      return;
    }
    this.sendingText = true;
    this.textDocumentService
      .sendText({
        recipients: this.recipients,
        text_document: {
          text_content: this.textContent,
          text_template_id: this.selectedTemplateId,
        },
      })
      .then((response_json) => {
        this.sendingText = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Text sent', 'success');
          this.reset();
          this.loadTextDocuments();
        } else this.notyService.notify(response_json.text || 'Error sending text', 'error');
      })
      .catch((err) => {
        this.sendingText = false;
        this.notyService.notify(`Err sendinf text: ${err}`);
      });
  }
}
